import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  TextLink,
  theme,
  TitleText,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  useKustomerAvailability,
  useKustomerContactUsHandler,
} from '../../integrations/kustomer';

export const OrderStatusContactUs = () => {
  const { currentBreakpoint } = useResponsive();
  const isKustomerAvailable = useKustomerAvailability();
  const { handleContactUs, handlingContactUs } = useKustomerContactUsHandler(
    isKustomerAvailable,
    'order-status',
  );

  const props: ContactUsProps = {
    onContactUsPress: handleContactUs,
    loading: handlingContactUs,
  };

  if (currentBreakpoint.isXS) return <MobileContactUs {...props} />;

  return <DesktopContactUs {...props} />;
};

const DesktopContactUs = ({ onContactUsPress, loading }: ContactUsProps) => {
  const { t } = useLocalizationContext();

  return (
    <View style={[styles.contactUsContainerDesktop, styles.row]}>
      <BodyText style={styles.contactUsText}>
        <BodyText size={4} bold>
          {t('order-status-info.contact-us-question')}
        </BodyText>
        <TextLink
          style={[styles.contactUsText, styles.contactUsCta]}
          size={4}
          disabled={loading}
          onPress={onContactUsPress}
        >
          {t('order-status-info.contact-us-cta')}
        </TextLink>
      </BodyText>
    </View>
  );
};

const MobileContactUs = ({ onContactUsPress, loading }: ContactUsProps) => {
  const { t } = useLocalizationContext();

  return (
    <>
      <View style={styles.contactUsDividerMobile} />
      <View style={styles.contactUsContainerMobile}>
        <TitleText style={styles.contactUsTitle} size={5}>
          {t('order-status-info.contact-us-question')}
        </TitleText>
        <BodyText style={styles.contactUsTextMobile} size={4}>
          {t('order-status-info.contact-us-answer-mobile-1')}
        </BodyText>
        <Button
          palette="primary"
          style={styles.mobileContactUsButton}
          onPress={onContactUsPress}
          isLoading={loading}
        >
          {t('order-status-info.contact-us-cta')}
        </Button>
      </View>
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  contactUsContainerDesktop: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderTopWidth: 1,
    borderTopColor: theme.colors.GREEN_1,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing['6'],
  },
  contactUsContainerMobile: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing['6'],
  },
  contactUsDividerMobile: {
    marginTop: theme.spacing['6'],
    marginBottom: theme.spacing['10'],
    backgroundColor: theme.colors.NEUTRAL_3,
    height: 1,
    width: '100%',
  },
  contactUsTitle: {
    marginBottom: theme.spacing['2'],
  },
  contactUsTextMobile: {
    lineHeight: 20,
    textAlign: 'center',
  },
  contactUsText: {
    lineHeight: 20,
  },
  contactUsCta: {
    marginHorizontal: theme.spacing['1'],
  },
  mobileContactUsButton: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContactUsProps = {
  readonly onContactUsPress: () => void;
  readonly loading: boolean;
};
